/**
 * useSubscriptionModal — opens payment/subscription modals hosted in scio1 iframe.
 *
 * Three modal types:
 * 1. openAddOnRequired — add-on license limit (AI, Sensor, Video Wall, Background Music)
 * 2. openSubscriptionPlan — plan upgrade needed (e.g., Pro Plus+ for Remote Control)
 * 3. openRequiredPlan — specific plan required (e.g., Engage for interactive assets)
 *
 * Uses Scio1Frame mode="modal" (hidden iframe) for postMessage bridge.
 * When embedded in scio1, falls back to iframe-child-bridge.
 *
 * @see claude-dev/2026-02-09-screen-ted/final-document/fundamentals/payment-modal.md
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useApolloClient } from '@apollo/client/react';
import { DEVICE_CODE_STATUS, ROLE_TYPES } from '@scio3/shared-types';
import { GET_COMPANY_INFO } from '@scio3/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { useScio1ModalStore } from '@/stores/scio1-modal-store';
import { useSmallAppEditorStore } from '@/stores/smallapp-editor-store';
import { isInIframeContext, sendToParentWithPromise } from '@/lib/iframe-child-bridge';
import { useBillingDialogStore } from '@/features/billing/stores/billing-dialog-store';

const useNativeBilling = import.meta.env.VITE_USE_NATIVE_BILLING === 'true';

// --- Response types from scio1 modals ---

export interface AddOnRequiredResponse {
  status?: boolean;
  type?: 'goToSub';
  close?: boolean;
}

// ---------------------------------------------------------------------------
// Native-billing promise plumbing for openAddOnRequired (PW02)
//
// scio1's add-ons modal awaits the user's decision (trial / add licenses /
// cancel) and resolves with {status: true} when a purchase completed so the
// caller can retry the original mutation (e.g. addWidgetAddonDevices). The
// native scio3 path used to return {close: true} synchronously, breaking
// that retry.
//
// The dialog components call `resolveAddonRequired()` on user action —
// AddonRequiredDialog resolves on Cancel/Trial; AddonUpdateDialog resolves
// when it closes after being chained from AddonRequiredDialog (Add Licenses
// path). Only one native addon-required flow can be active at a time, so a
// module-scope resolver is sufficient.
// ---------------------------------------------------------------------------

let nativeAddOnPendingResolve: ((result: AddOnRequiredResponse) => void) | null = null;

export function resolveAddonRequired(result: AddOnRequiredResponse): void {
  const resolve = nativeAddOnPendingResolve;
  nativeAddOnPendingResolve = null;
  resolve?.(result);
}

export interface SubscriptionPlanResponse {
  type?: 'goToSub';
  [key: string]: unknown;
}

// --- Error extraction ---

interface GraphQLErrorExtension {
  status?: boolean;
  items?: unknown[];
  [key: string]: unknown;
}

/**
 * Get the first GraphQL error from an Apollo error.
 * Apollo Client 4 CombinedGraphQLErrors uses `errors`, not `graphQLErrors`.
 * We check both for compatibility.
 */
function getFirstGqlError(error: unknown):
  | {
      extensions?: Record<string, unknown>;
      message?: string;
    }
  | undefined {
  const err = error as {
    graphQLErrors?: Array<{ extensions?: Record<string, unknown>; message?: string }>;
    errors?: Array<{ extensions?: Record<string, unknown>; message?: string }>;
  };
  return err?.graphQLErrors?.[0] ?? err?.errors?.[0];
}

/**
 * Extract error extension from a GraphQL error for plan/addon gating detection.
 *
 * NestJS HttpExceptions wrap error data in extensions.exception.response.
 * Always prefer the nested response when available — it contains the clean data shape
 * (status, isCompanyTrial, haveCard, maxQuantity, etc.) that scio1 modals expect.
 * This matches scio2's getExtensionGraphQL: error.graphQLErrors[0].extensions.exception.response
 *
 * Returns the extension data if it's a plan/addon error, null otherwise.
 */
export function getGraphQLErrorExtension(error: unknown): GraphQLErrorExtension | null {
  const gqlError = getFirstGqlError(error);
  if (!gqlError) return null;

  const ext = gqlError.extensions;
  if (!ext) return null;

  // NestJS HttpException wraps error data in extensions.exception.response
  // Prefer nested response (clean data shape for scio1 modals), fall back to top-level extensions
  const exception = ext.exception as Record<string, unknown> | undefined;
  const nestedResponse = exception?.response as Record<string, unknown> | undefined;

  // Check top-level code for explicitly coded errors
  const code = ext.code as string | undefined;
  if (
    code === 'REQUIRED_PLAN' ||
    code === 'ADDON_ENABLE_TRIAL_NOT_ENOUGH' ||
    code === 'ADDON_ENABLE_PAID_NOT_ENOUGH'
  ) {
    return (nestedResponse ?? ext) as GraphQLErrorExtension;
  }

  // Check nested exception.response for NestJS HttpException errors (ADDON_NOT_ENOUGH, etc.)
  // Backend throws NotAcceptableException → Apollo wraps as INTERNAL_SERVER_ERROR
  if (nestedResponse && nestedResponse.status === false) {
    return nestedResponse as GraphQLErrorExtension;
  }

  return null;
}

/**
 * Extract the error code from a GraphQL error.
 */
export function getGraphQLErrorCode(error: unknown): string | undefined {
  const gqlError = getFirstGqlError(error);
  return gqlError?.extensions?.code as string | undefined;
}

export function useSubscriptionModal() {
  const navigate = useNavigate();
  const client = useApolloClient();
  const isEmbedded = useAuthStore((s) => s.isEmbedded);

  const sendToScio1 = useCallback(
    async <T = unknown>(event: string, data: unknown): Promise<T> => {
      // When embedded inside scio1, use parent bridge
      if (isEmbedded && isInIframeContext) {
        return sendToParentWithPromise<T>(event, data, 300000);
      }

      // Normal mode: use the adapter iframe.
      // Wait up to 45s for the bridge to register — the modal adapter loads adapterPortalSecond
      // from scio1 via DDP, which can take 10-30s on first load. Without this wait, clicking
      // Upgrade immediately after login silently does nothing (sendWithPromise is still null).
      const waitForBridge = (): Promise<void> =>
        new Promise((resolve, reject) => {
          if (useScio1ModalStore.getState().sendWithPromise) {
            resolve();
            return;
          }
          const timeout = setTimeout(() => {
            unsubscribe();
            reject(new Error('scio1 modal adapter not ready after 45s'));
          }, 45_000);
          const unsubscribe = useScio1ModalStore.subscribe((state) => {
            if (state.sendWithPromise) {
              clearTimeout(timeout);
              unsubscribe();
              resolve();
            }
          });
        });

      await waitForBridge();
      const { sendWithPromise, postToIframe, showModal } = useScio1ModalStore.getState();

      // Ensure the iframe is on the adapter page before opening any modal.
      // scio1 may have navigated away; this mirrors scio2's routerRedirect({ url: METEOR_MAIN_ADAPTER_URL }).
      postToIframe?.({
        event: 'ROUTER_REDIRECT',
        data: { url: 'app.adapterPortalSecond' },
      });

      showModal();
      try {
        // sendWithPromise is guaranteed non-null here — waitForBridge() resolved only after it was set
        if (!sendWithPromise) throw new Error('sendWithPromise unavailable after bridge init');
        const result = await sendWithPromise<T>(event, data);
        return result;
      } finally {
        useScio1ModalStore.getState().hideModal();
      }
    },
    [isEmbedded],
  );

  /**
   * Open the Add-On Required modal in scio1.
   * Called when addWidgetAddonDevices / toggleDeviceFeature fails with addon license errors.
   * Backend error extension may already contain an `items` array — send directly to avoid double-wrapping.
   */
  const openAddOnRequired = useCallback(
    async (errorData: unknown): Promise<AddOnRequiredResponse> => {
      // Native billing: open AddonRequiredDialog instead of scio1 iframe.
      // Return a promise that resolves when the dialog flow completes — on
      // successful trial / license purchase we resolve {status: true} so the
      // caller (e.g. AddOnConfigDialog) retries the original mutation. Any
      // previously-pending resolver is cancelled first to avoid leaks.
      if (useNativeBilling) {
        const data = errorData as Record<string, unknown>;
        if (nativeAddOnPendingResolve) {
          nativeAddOnPendingResolve({ close: true });
          nativeAddOnPendingResolve = null;
        }
        // Two backend payload shapes feed this dialog:
        //   1. addWidgetAddonDevices / toggleDeviceFeature → addonKey/codeStatus/etc. at TOP level
        //      (device.service.ts:3939 throw body)
        //   2. updateDevice (Edit Screen → Save) → throws {...checkAddOnContentHavePermission, message}
        //      where addonKey/code/isCompanyTrial/haveCard/maxQuantityNeedIncrease live INSIDE items[0]
        //      (device.service.ts:1005 spreads checkAddOnContentHavePermissionAssignToScreen result,
        //      whose shape is { status, code, items: [...] } — no top-level addOnKey).
        // Without the items[0] fallback, the Edit Screen path landed an empty addonType in the
        // dialog — the popup rendered with blank {{name}} placeholders and the Activate Trial /
        // Add Licenses buttons silently no-op'd because their handlers early-return on !addonKey.
        const items = (data?.items as Array<Record<string, unknown>> | undefined) ?? [];
        const firstItem = items[0];
        useBillingDialogStore.getState().openDialog('addonRequired', {
          addonType: data?.addOnKey ?? data?.key ?? firstItem?.addOnKey ?? firstItem?.key,
          devices: data?.devices,
          items: data?.items,
          // SP49 — exception-path payloads can carry `maxQuantityNeedIncrease` at either the
          // top level (addWidgetAddonDevices) OR inside items[0] (updateDevice). Scio1 parity:
          // aericastAssignModal uses `items[0].maxQuantityNeedIncrease || items[0].devices.length`.
          maxQuantityNeedIncrease:
            data?.maxQuantityNeedIncrease ?? firstItem?.maxQuantityNeedIncrease,
          // SP52 — forward backend signals so AddonRequiredDialog can decide
          // between "Add Licenses" (in-place purchase) and "Manage Licenses"
          // (navigate to subscription page). Matches scio1's aericastAssignModal
          // ng-if condition referencing codeStatus / isCompanyTrial / haveCard /
          // items[0].maxQuantity.
          // #1174 — also forward `isTrial` so AddonRequiredDialog can suppress
          // the Activate Trial CTA when the user already holds a paid add-on
          // subscription (backend code 1000 is overloaded for both trial-available
          // and paid-sub-no-devices, but `isTrial` disambiguates them).
          codeStatus: data?.code ?? firstItem?.code,
          isTrial: data?.isTrial ?? firstItem?.isTrial,
          isCompanyTrial: data?.isCompanyTrial ?? firstItem?.isCompanyTrial,
          haveCard: data?.haveCard ?? firstItem?.haveCard,
        });
        return new Promise<AddOnRequiredResponse>((resolve) => {
          nativeAddOnPendingResolve = resolve;
        });
      }

      try {
        // Backend error extension may already contain an `items` array with addon details
        // (addOnKey, devices, etc.). If so, send it directly — don't double-wrap.
        // If it doesn't have items, wrap it as [errorData] for scio1's expected format.
        const data = errorData as Record<string, unknown>;
        const base = Array.isArray(data?.items)
          ? (errorData as Record<string, unknown>)
          : { items: [errorData] };
        // Pass isRootAdmin from scio3 — the modal-mode iframe's `activeLogin` may not be populated
        // in time, so principal.isRootAdmin() can falsely return false and show "Can't upgrade"
        // to Owners. We trust scio3's auth context as the source of truth and let the modal
        // override the principal check with this flag.
        const rootRole = useAuthStore.getState().me?.rootRole;
        const isRootAdmin =
          rootRole === ROLE_TYPES.OWNER ||
          rootRole === ROLE_TYPES.ADMIN ||
          rootRole === ROLE_TYPES.SUPER_ADMIN;
        const payload = { ...base, isRootAdmin };
        const response = await sendToScio1<AddOnRequiredResponse>('OPEN_ADD_REQUIRED', payload);
        return response ?? { close: true };
      } catch {
        return { close: true };
      }
    },
    [sendToScio1],
  );

  /**
   * Open the Subscription Plan modal in scio1.
   * Called when a feature requires a higher plan.
   */
  const openSubscriptionPlan = useCallback(
    async (data: {
      title?: string;
      subtitle?: string;
      // Native-wizard-only title override. Distinct from `title`/`subtitle`
      // (which are forwarded to the legacy scio1 iframe modal for backward
      // compatibility). Only proactive callers (e.g. PromotionalBanner) should
      // set `wizardTitle`; feature-gate callers leave it unset so the wizard
      // falls back to its default "Upgrade Required" / "Update Subscription Plan"
      // ternary in `SubscriptionWizardDialog`.
      wizardTitle?: string;
      wizardSubtitle?: string;
      action?: string;
      sizeModal?: string;
      params?: Record<string, unknown>;
    }): Promise<SubscriptionPlanResponse> => {
      // Native billing: open SubscriptionWizardDialog instead of scio1 iframe
      if (useNativeBilling) {
        useBillingDialogStore.getState().openDialog('subscriptionWizard', {
          // Forward only the wizard-specific overrides — NOT the legacy `title`/
          // `subtitle` args (which existing callers use for the scio1 iframe modal
          // and would otherwise unintentionally override the wizard's default
          // "Upgrade Required" with their iframe-specific copy).
          wizardTitle: data.wizardTitle,
          wizardSubtitle: data.wizardSubtitle,
          planFeature: data.params?.planFeature,
          minPlanRequired: data.params?.minPlanRequired,
          // Opt-in caller signal: which plan to highlight on the plan-comparison
          // step. Distinct from `planFeature` (which feature-gate callers overload
          // to echo the user's current plan). Only set by callers that explicitly
          // want a recommendation surfaced (e.g. PromotionalBanner → 'proPlus').
          recommendedPlan: data.params?.recommendedPlan,
          subAccountId: data.params?.subAccountId,
          teamId: data.params?.teamId,
        });
        return {};
      }

      try {
        const response = await sendToScio1<SubscriptionPlanResponse>(
          'OPEN_SUBSCRIPTION_MODAL',
          data,
        );
        return response ?? {};
      } catch {
        return {};
      }
    },
    [sendToScio1],
  );

  /**
   * Open the Required Plan modal in scio1.
   * Called when an operation requires a specific plan (e.g., Engage).
   */
  const openRequiredPlan = useCallback(
    async (data: Record<string, unknown>): Promise<SubscriptionPlanResponse> => {
      // Native billing: SCIO1 parity (#1181) — show the "Upgrade Required" confirm
      // dialog FIRST with the current plan + affected asset list. Clicking
      // Update Subscription opens the wizard; Cancel closes the dialog.
      if (useNativeBilling) {
        // Prefer `data.plan` (= backend's featureRequired — the plan needed to
        // USE/PUSH this content) over `data.minPlanRequired` (asset's INTRINSIC
        // floor — what's needed to CREATE/OWN it). They diverge for content
        // gated on app-type rather than asset-tier — e.g. a Designer asset has
        // assets[i].minPlanRequired="free" (anyone can have one in their
        // library) but plan="engage" (only Engage can render it). Reading
        // the asset's minPlanRequired routed the dialog to Pro Plus instead
        // of Engage (#1186 follow-up).
        const requiredPlan = (data.plan as string) || (data.minPlanRequired as string) || '';
        if (!requiredPlan) return {};
        // Resolve the user's current plan from GetCompanyInfo — the SAME source
        // the plan banner uses and the one the downgrade refetches. Do NOT read
        // `me.payment.planFeature`: ME_QUERY's `payment` is an untyped JSON scalar
        // that can lag behind the company's real plan (e.g. it kept reporting
        // "Pro Plus" after a downgrade to Free while the banner already showed
        // Free) — #1354. Fall back to the auth-store company snapshot on a cache miss.
        let currentPlan = '';
        try {
          const companyCache = client.readQuery<{
            getCompanyInfo?: { payment?: { planFeature?: string } | null };
          }>({ query: GET_COMPANY_INFO });
          currentPlan = companyCache?.getCompanyInfo?.payment?.planFeature ?? '';
        } catch {
          // cache miss — fall through to the auth-store company snapshot below
        }
        if (!currentPlan) {
          const fromCompany = useAuthStore.getState().company?.payment?.planFeature;
          if (typeof fromCompany === 'string') currentPlan = fromCompany;
        }
        useBillingDialogStore.getState().openDialog('upgradeRequired', {
          minPlanRequired: requiredPlan,
          currentPlan,
          assets: data.assets,
        });
        return {};
      }

      try {
        const response = await sendToScio1<SubscriptionPlanResponse>(
          'OPEN_SUBSCRIPTION_MODAL_REQUIRED_PLAN',
          data,
        );
        return response ?? {};
      } catch {
        return {};
      }
    },
    [sendToScio1, client],
  );

  /**
   * Handle a toggleFeature error with subscription modal flow.
   *
   * Returns a three-state result:
   * - `'retry'`     — user purchased addon, caller should retry the mutation
   * - `'handled'`   — user saw the modal and dismissed it, caller should stay silent
   * - `'unhandled'` — not a subscription/addon error, caller should show a generic error
   *
   * @param error — Apollo GraphQL error
   * @param devices — optional devices array for the addon modal (scio1 AericastAssignModal needs it).
   *   Format: `[{ _id, name }]`. The error extension often lacks this, so callers pass it in.
   */
  const handleToggleError = useCallback(
    async (
      error: unknown,
      devices?: Array<{ _id: string; name: string }>,
    ): Promise<'retry' | 'handled' | 'unhandled'> => {
      const ext = getGraphQLErrorExtension(error);
      if (!ext) return 'unhandled';

      // Backend returns numeric code from DEVICE_CODE_STATUS in the error extension.
      // Check both the extension's numeric code and the GraphQL error code string as fallback.
      const numericCode = (ext as Record<string, unknown>).code as number | undefined;
      const gqlCode = getGraphQLErrorCode(error);

      if (numericCode === DEVICE_CODE_STATUS.REQUIRED_PLAN || gqlCode === 'REQUIRED_PLAN') {
        // Plan upgrade needed — open the "Required Plan" modal in scio1 (engagePlanConfirmModal).
        // This shows a dialog with plan info, asset list, and "Update Subscription" / "Cancel" buttons,
        // matching scio1/scio2 behavior. Uses OPEN_SUBSCRIPTION_MODAL_REQUIRED_PLAN event (not the
        // generic OPEN_SUBSCRIPTION_MODAL which skips the confirmation dialog and shows the full plan page).
        const response = await openRequiredPlan(ext as Record<string, unknown>);

        if (response?.type === 'goToSub') {
          // Close any open SmallApp editor (BM form, etc.) so it doesn't linger after the user
          // navigates back from the subscription page. Safe no-op if already closed.
          useSmallAppEditorStore.getState().closeEditor();
          navigate('/app/s/subscription-plan');
        }
        return 'handled'; // Plan changes require page reload, don't retry
      }

      if (
        numericCode === DEVICE_CODE_STATUS.ADDON_ENABLE_TRIAL_NOT_ENOUGH ||
        numericCode === DEVICE_CODE_STATUS.AERICAST_PAID_NOT_ENOUGH ||
        numericCode === DEVICE_CODE_STATUS.AERICAST_TRIAL_NOT_ENOUGH ||
        numericCode === DEVICE_CODE_STATUS.ADDON_NOT_ENOUGH ||
        numericCode === DEVICE_CODE_STATUS.ADDON_TRIAL_NOT_ENOUGH ||
        numericCode === DEVICE_CODE_STATUS.AERICAST_TRIAL ||
        numericCode === DEVICE_CODE_STATUS.ADDON_TIER_MISMATCH ||
        gqlCode === 'ADDON_ENABLE_TRIAL_NOT_ENOUGH' ||
        gqlCode === 'ADDON_ENABLE_PAID_NOT_ENOUGH'
      ) {
        // Add-on license limit — open add-on required modal
        // Merge caller-provided devices into ext (error extension often lacks device info)
        const extWithDevices = devices ? { ...ext, devices } : ext;
        const response = await openAddOnRequired(extWithDevices);

        if (response.status === true) {
          return 'retry'; // User purchased — retry toggle
        }
        if (response.type === 'goToSub') {
          useSmallAppEditorStore.getState().closeEditor();
          navigate('/app/s/subscription-plan');
        }
        return 'handled'; // User dismissed or navigated away
      }

      return 'unhandled';
    },
    [navigate, openRequiredPlan, openAddOnRequired],
  );

  return {
    openAddOnRequired,
    openSubscriptionPlan,
    openRequiredPlan,
    handleToggleError,
  };
}
